import type { StockListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { Button, Card, Icon } from "@sellar-io/components";
import { Fragment, useEffect } from "react";
import { ProfilePicture } from "../../../components";
import {
  useUnifiedCheckoutBasketAddItem,
  useUnifiedCheckoutBasketUpdateItem,
} from "../../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useStockList } from "../../../hooks/api/v2/stockList/useStockList";
import { getParentIdsForBadgesFromOrder } from "../../stockList/categoryPage/CategoryBadgesDialog/CategoryBadgesDialog.shared";
import type {
  SupplierOrderEntry,
  UnifiedCheckoutDraftItem,
} from "./UnifiedCheckoutOverviewPage.shared";

export const SupplierKegBadges = ({
  entry,
  basketItems,
  basketUuid,
  onAvailabilityChange,
}: {
  entry: SupplierOrderEntry;
  basketItems: UnifiedCheckoutDraftItem[];
  basketUuid: string;
  onAvailabilityChange?: (available: boolean) => void;
}) => {
  const { order, supplierInfo } = entry;
  const specificParentIds = (
    getParentIdsForBadgesFromOrder(order) ?? []
  ).filter((id): id is number => typeof id === "number");
  const badgeProductsQuery = useStockList(
    {
      ParentId: null,
      active: true,
      SupplierId: order.SupplierId ?? 0,
      isPreview: false,
      productTypeOnly: "badge",
      specificParentIds,
    },
    { enabled: specificParentIds.length > 0 },
  );
  const addItem = useUnifiedCheckoutBasketAddItem();
  const updateItem = useUnifiedCheckoutBasketUpdateItem();
  const badgeProducts =
    badgeProductsQuery.data?.pages.flatMap((page) => page) ?? [];
  const badges = badgeProducts.flatMap((badgeProduct) =>
    badgeProduct.Products.map((badge) => ({
      ...badge,
      parent: badgeProduct,
    })),
  );

  useEffect(() => {
    onAvailabilityChange?.(specificParentIds.length > 0 && badges.length > 0);
  }, [badges.length, onAvailabilityChange, specificParentIds.length]);

  if (specificParentIds.length === 0 || badges.length === 0) return null;

  const changeQuantity = ({
    badge,
    quantity,
  }: {
    badge: StockListResponseType[number]["Products"][number];
    quantity: number;
  }) => {
    const basketItem = basketItems.find((item) => item.ProductId === badge.id);
    if (basketItem) {
      updateItem.mutate({ basketUuid, itemId: basketItem.id, quantity });
      return;
    }
    if (quantity === 0 || order.SupplierId == null) return;

    addItem.mutate({
      SupplierId: order.SupplierId,
      ProductId: badge.id,
      quantity,
      expectedDeliveryAt: order.expectedDeliveryDate
        ? new Date(order.expectedDeliveryDate)
        : null,
      orderCutoffAt: order.orderCutoffDate
        ? new Date(order.orderCutoffDate)
        : null,
    });
  };

  return (
    <Card className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 shadow-sm">
      <div className="mb-3">
        <p className="text-sm text-zinc-600">
          Let {supplierInfo.supplier.Business.name} know how many keg badges to
          send with your order
        </p>
      </div>
      <div className="flex flex-col gap-2">
        {badges.map((badge) => {
          const quantity =
            basketItems.find((item) => item.ProductId === badge.id)?.quantity ??
            0;
          const badgeSegments = (badge.name ?? "").split(" • ").filter(Boolean);
          const isPending =
            (addItem.isPending && addItem.variables?.ProductId === badge.id) ||
            (updateItem.isPending &&
              basketItems.find((item) => item.ProductId === badge.id)?.id ===
                updateItem.variables?.itemId);

          return (
            <div
              key={badge.id}
              className="flex items-center justify-between gap-3 rounded-lg bg-white p-3"
            >
              <div className="flex min-w-0 items-center gap-2">
                <ProfilePicture
                  logoUrl={badge.parent.imageUrl}
                  fallback="product-badge"
                  size="small"
                />
                <span className="truncate text-sm">
                  {badgeSegments.map((segment, index) => (
                    <Fragment key={`${segment}-${index}`}>
                      <span className={index === 0 ? "font-semibold" : ""}>
                        {segment}
                      </span>
                      {index < badgeSegments.length - 1 && " • "}
                    </Fragment>
                  ))}
                </span>
              </div>
              <div className="flex flex-none items-center gap-3">
                <span className="text-sm font-semibold">
                  {formatCurrency(
                    badge.ProductPriceTier?.fullPrice ?? 0,
                    order.currency ?? "gbp",
                  )}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    size="small"
                    className="p-2.5"
                    disabled={quantity === 0 || isPending}
                    onClick={() =>
                      changeQuantity({
                        badge,
                        quantity: Math.max(0, quantity - 1),
                      })
                    }
                  >
                    <Icon icon="MINUS" size={0.6} />
                  </Button>
                  <span className="flex h-9 min-w-9 items-center justify-center rounded-lg border border-zinc-300 bg-white text-sm font-medium">
                    {quantity}
                  </span>
                  <Button
                    size="small"
                    className="p-2.5"
                    disabled={isPending}
                    onClick={() =>
                      changeQuantity({ badge, quantity: quantity + 1 })
                    }
                  >
                    <Icon icon="PLUS" size={0.6} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
